/**
 * @file
 * Incremental computation.
 *
 * An incremental computation leverages the previous computation's state to
 * produce the next result, so unchanged inputs can reuse prior results
 * (referentially-stable outputs that play well with `React.memo`).
 *
 * The pattern is continuation-passing: an `IncFunction` does not just return a
 * value — it returns an `IncResult` holding both the `value` and a `next`
 * function. You feed the next input into `next`, and that `next` carries
 * whatever internal state the previous step accumulated.
 *
 * @example
 * let fn = incCache((n: number) => n * 2);
 * let { value, next } = fn(1); // value = 2
 * ({ value, next } = next(1)); // value = 2, cached
 * ({ value, next } = next(3)); // value = 6, recomputed
 *
 * Combinators (`incArray`, `incMap`, `incObject`, `incOptional`, `incCompose`)
 * build larger incremental computations out of smaller ones while preserving
 * this state-threading and caching at each node.
 */

import { Equal, identityEqual } from "./equal";
import { objectEntries } from "./object";

/**
 * Incremental function.
 * @param input Input value
 * @returns Result
 */
export type IncFunction<Input, Output> = (
  input: Input,
) => IncResult<Input, Output>;

/** Extracts the input type of an {@link IncFunction}. */
export type IncFunctionInput<T> =
  T extends IncFunction<infer Input, any> ? Input : never;

/** Extracts the output type of an {@link IncFunction}. */
export type IncFunctionOutput<T> =
  T extends IncFunction<any, infer Output> ? Output : never;

/**
 * Result of an incremental computation
 */
export interface IncResult<A, B> {
  /**
   * Next function. This can leverage internal state of the previous computation.
   */
  next: IncFunction<A, B>;
  /** Value. */
  value: B;
}

/**
 * Wrap an incremental function so that re-running with an `equal` input reuses
 * the previous result.
 * @param inner Incremental function.
 * @param equal Equality comparison.
 */
export function cacheResult<Input, Output>(
  inner: IncFunction<Input, Output>,
  equal: Equal<Input>,
): IncFunction<Input, Output> {
  return (function fn(inner) {
    return (input) => {
      const { next, value } = inner(input);
      const nextFn = fn(next);
      const result: IncResult<Input, Output> = {
        value,
        next: (nextInput) =>
          equal(input, nextInput) ? result : nextFn(nextInput),
      };
      return result;
    };
  })(inner);
}

export type ArrayKeyFn<Element, Key> = (element: Element, index: number) => Key;

/**
 * Array of incremental values.
 * @param elementFn Incremental function factory for elements.
 * @param keyFn Key function, to track elements between computations.
 */
export function incArray<Key, InputElement, OutputElement>(
  elementFn: (key: Key) => IncFunction<InputElement, OutputElement>,
  keyFn: ArrayKeyFn<InputElement, Key>,
): IncFunction<InputElement[], OutputElement[]> {
  return (function result(
    existing: Map<Key, IncFunction<InputElement, OutputElement>>,
  ) {
    return cacheResult(
      (input: InputElement[]): IncResult<InputElement[], OutputElement[]> => {
        const nexts: typeof existing = new Map();
        const values: OutputElement[] = [];
        for (const [index, element] of input.entries()) {
          const key = keyFn(element, index);
          const fn = existing.get(key) ?? elementFn(key);
          const { next, value } = fn(element);
          nexts.set(key, next);
          values.push(value);
        }
        return { value: values, next: result(nexts) };
      },
      identityEqual,
    );
  })(new Map());
}

/**
 * Chain incremental functions.
 * @param a First function
 * @param b Second function
 */
export function incCompose<A, B, C>(
  a: IncFunction<A, B>,
  b: IncFunction<B, C>,
): IncFunction<A, C> {
  return (input) => {
    const { next: nextA, value: valueA } = a(input);
    const { next: nextB, value: valueB } = b(valueA);
    return { value: valueB, next: incCompose(nextA, nextB) };
  };
}

/**
 * A cached function.
 */
export function incCache<Input, Output>(
  fn: (input: Input) => Output,
  equal: Equal<Input> = identityEqual,
): IncFunction<Input, Output> {
  return function next(input): IncResult<Input, Output> {
    return cacheResult(
      (input: Input) => ({ value: fn(input), next }),
      equal,
    )(input);
  };
}

/**
 * Map of incremental values.
 * @param valueFn Incremental function factory for values.
 */
export function incMap<Key, InputValue, OutputValue>(
  valueFn: (key: Key) => IncFunction<InputValue, OutputValue>,
): IncFunction<Map<Key, InputValue>, Map<Key, OutputValue>> {
  return (function result(
    existing: Map<Key, IncFunction<InputValue, OutputValue>>,
  ) {
    return cacheResult(
      (
        input: Map<Key, InputValue>,
      ): IncResult<Map<Key, InputValue>, Map<Key, OutputValue>> => {
        const nexts: typeof existing = new Map();
        const values = new Map<Key, OutputValue>();
        for (const [key, v] of input) {
          const f = existing.get(key) || valueFn(key);
          const { next, value } = f(v);
          nexts.set(key, next);
          values.set(key, value);
        }
        return { value: values, next: result(nexts) };
      },
      identityEqual,
    );
  })(new Map());
}

/**
 * Object with incremental property values
 */
export function incObject<
  Properties extends { [key: string]: IncFunctionInput<any> },
>(
  properties: Properties,
): IncFunction<ObjectInput<Properties>, ObjectOutput<Properties>> {
  return cacheResult((input) => {
    const nexts = {} as Properties;
    const values = {} as ObjectOutput<Properties>;
    for (const [key, f] of objectEntries(properties)) {
      const { next, value } = (f as IncFunction<any, any>)(input[key]);
      nexts[key] = next as Properties[keyof Properties];
      values[key] = value;
    }
    return { value: values, next: incObject(nexts) };
  }, identityEqual);
}

type ObjectInput<Properties> = {
  [Key in keyof Properties]: IncFunctionInput<Properties[Key]>;
};

type ObjectOutput<Properties> = {
  [Key in keyof Properties]: IncFunctionOutput<Properties[Key]>;
};

/**
 * Optional incremental function.
 * @param inner Incremental function for defined value.
 */
export function incOptional<Input, Output>(
  inner: IncFunction<Input, Output>,
): IncFunction<Input | undefined, Output | undefined> {
  return (function result(
    existing: IncFunction<Input, Output>,
  ): IncFunction<Input | undefined, Output | undefined> {
    return (input) => {
      if (input === undefined) {
        return { value: undefined, next: result(existing) };
      }
      return existing(input);
    };
  })(inner);
}
